import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import { setLocale, normalizeLocale } from '@/i18n'
import type { UserMeResponse } from '@/types/auth'

/**
 * Query key for identity queries
 */
export const identityKeys = {
  all: ['identity'] as const,
  me: () => [...identityKeys.all, 'me'] as const,
}

/**
 * Hook for fetching and caching current user identity
 */
export function useIdentity() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const setUser = useAuthStore((state) => state.setUser)

  const query = useQuery({
    queryKey: identityKeys.me(),
    queryFn: async () => {
      const response = await api.get<UserMeResponse>('/me')
      return response.data
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnWindowFocus: false,
  })

  // Sync user data to auth store and locale to i18next when fetched
  useEffect(() => {
    if (query.data && isAuthenticated) {
      const userData = query.data
      setUser({
        id: String(userData.id),
        email: userData.email,
        firstName: userData.first_name,
        lastName: userData.last_name,
        fullName: userData.fullName,
        role: userData.role,
        customerId: userData.customer_id ? String(userData.customer_id) : undefined,
        partnerId: userData.partner_id ? String(userData.partner_id) : undefined,
      })

      // Sync user's locale preference with i18next
      if (userData.locale) {
        const normalizedLocale = normalizeLocale(userData.locale)
        setLocale(normalizedLocale)
      }
    }
  }, [query.data, isAuthenticated, setUser])

  return {
    user: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

export default useIdentity
